import { ref, onMounted, onUnmounted, watch } from 'vue';

const MAX_TIMEOUT_MS = 604800000;

export function useInactivity(timeoutRef) {
  const isIdle = ref(false);
  let timeoutId = null;
  let lastActivityAt = Date.now();
  let windowBlurred = false;

  const isPageActive = () => !document.hidden && !windowBlurred;

  const clearTimer = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const scheduleDeadline = () => {
    clearTimer();
    if (!isPageActive()) return;

    const timeoutMs = Number(timeoutRef.value);
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return;

    const remainingMs = lastActivityAt + timeoutMs - Date.now();
    if (remainingMs <= 0) {
      isIdle.value = true;
      return;
    }

    timeoutId = setTimeout(
      () => {
        scheduleDeadline();
      },
      Math.min(remainingMs, MAX_TIMEOUT_MS)
    );
  };

  const resetTimer = () => {
    lastActivityAt = Date.now();
    isIdle.value = false;
    scheduleDeadline();
  };

  const handleWindowBlur = () => {
    windowBlurred = true;
    clearTimer();
  };

  const handleWindowFocus = () => {
    windowBlurred = false;
    resetTimer();
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      clearTimer();
      return;
    }

    resetTimer();
  };

  const activityEvents = [
    'pointerdown',
    'pointermove',
    'keydown',
    'scroll',
    'touchstart',
  ];

  const handleActivity = () => {
    if (isPageActive()) resetTimer();
  };

  onMounted(() => {
    activityEvents.forEach(event =>
      window.addEventListener(event, handleActivity, event === 'scroll')
    );
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetTimer();
  });

  onUnmounted(() => {
    activityEvents.forEach(event =>
      window.removeEventListener(event, handleActivity, event === 'scroll')
    );
    window.removeEventListener('blur', handleWindowBlur);
    window.removeEventListener('focus', handleWindowFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearTimer();
  });

  watch(timeoutRef, resetTimer);

  return { isIdle, resetTimer };
}
